import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  Gift,
  LockKeyhole,
  Share2,
  Sparkles,
  Ticket,
  Truck,
  UserPlus,
  Users,
} from 'lucide-react'
import { api, type ReferralStatus } from '../../lib/api'
import { copyText } from '../../lib/clipboard'
import {
  REFERRAL_ALREADY_LINKED_TEXT,
  REFERRAL_CLAIMED_TEXT,
  REFERRAL_CLAIM_HINT,
  REFERRAL_LINKED_NOTE,
  referralClaimErrorText,
} from '../../lib/referralClaim'
import { REFERRAL_SHARE, referralCodeText, shareMessageText } from '../../lib/shareMessages'
import { referralRewards, tierLabel } from '../referral/referralProgramMock'
import { MobileSheet, MobileState } from './MobileUI'

type MobileReferralProgramModalProps = {
  open: boolean
  onClose: () => void
}

type Banner = {
  tone: 'success' | 'warning'
  text: string
} | null

type LadderItem = {
  threshold: number
  /** trip = invitees with a finished, really-tracked trip; paid = invitees who paid for a tariff. */
  basis: 'trip' | 'paid'
  label: string
  title: string
  description: string
  reached: boolean
}


function getColleaguesLabel(count: number) {
  const lastTwo = count % 100
  const last = count % 10
  if (lastTwo >= 11 && lastTwo <= 14) return 'коллег'
  if (last === 1) return 'коллега'
  if (last >= 2 && last <= 4) return 'коллеги'
  return 'коллег'
}

function buildLadder(status: ReferralStatus): LadderItem[] {
  if (status.tiers?.length) {
    return status.tiers.map((tier) => ({
      threshold: tier.threshold,
      basis: tier.basis ?? 'trip',
      label: tierLabel(tier.threshold),
      title: tier.title,
      description: tier.description,
      reached: tier.reached,
    }))
  }

  return referralRewards.map((tier) => ({
    ...tier,
    basis: 'trip' as const,
    reached: status.qualified_count >= tier.threshold,
  }))
}

export function MobileReferralProgramModal({ open, onClose }: MobileReferralProgramModalProps) {
  const [status, setStatus] = useState<ReferralStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [banner, setBanner] = useState<Banner>(null)
  const [claimCode, setClaimCode] = useState('')
  const [claiming, setClaiming] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setStatus(await api.getReferral())
    } catch {
      setError('Не удалось загрузить реферальную программу.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    setBanner(null)
    void load()
  }, [load, open])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, open])

  const ladder = useMemo(() => (status ? buildLadder(status) : []), [status])
  const qualifiedCount = status?.qualified_count ?? 0
  const nextTier = ladder.find((tier) => !tier.reached) ?? null
  // Each rung counts its own basis: the free slot counts finished trips, the money rungs count
  // invitees who paid — so «осталось N» must not promise a coupon for trips alone.
  const paidCount = status?.paid_count ?? 0
  const nextTierRemaining = nextTier
    ? Math.max(nextTier.threshold - (nextTier.basis === 'paid' ? paidCount : qualifiedCount), 0)
    : 0
  const mainBonusUnlocked = Boolean(status?.bonus_active) || qualifiedCount >= 1
  const inviteValue = status?.link || status?.code || ''

  const handleCopy = async () => {
    if (!inviteValue) return
    const copied = await copyText(inviteValue)
    setBanner({
      tone: copied ? 'success' : 'warning',
      text: copied ? 'Ссылка для приглашения скопирована.' : 'Не удалось скопировать ссылку.',
    })
  }

  const handleShare = async () => {
    if (!inviteValue) return
    const text = status?.link
      ? shareMessageText(REFERRAL_SHARE, status.link)
      : referralCodeText(status?.code ?? '')

    if (!navigator.share) {
      await handleCopy()
      return
    }

    try {
      await navigator.share({ title: REFERRAL_SHARE.subject, text })
      setBanner({ tone: 'success', text: 'Приглашение отправлено.' })
    } catch (shareError) {
      if (shareError instanceof DOMException && shareError.name === 'AbortError') return
      setBanner({ tone: 'warning', text: 'Не удалось открыть меню отправки.' })
    }
  }

  const handleClaim = async () => {
    const code = claimCode.trim()
    if (!code) return
    setClaiming(true)
    setBanner(null)
    try {
      const result = await api.claimReferral(code)
      setClaimCode('')
      setBanner({
        tone: 'success',
        text: result.already_referred ? REFERRAL_ALREADY_LINKED_TEXT : REFERRAL_CLAIMED_TEXT,
      })
      await load()
    } catch (claimError) {
      setBanner({ tone: 'warning', text: referralClaimErrorText(claimError) })
    } finally {
      setClaiming(false)
    }
  }

  return (
    <MobileSheet
      open={open}
      title="Пригласить коллегу"
      subtitle="Бонусы за активные приглашения"
      onClose={onClose}
    >
      {loading && !status ? (
        <MobileState loading title="Загружаем бонусы" description="Загружаем бонусы…" />
      ) : null}

      {error && !status ? (
        <MobileState
          title={error}
          description="Проверьте подключение и попробуйте ещё раз."
          tone="danger"
          actionLabel="Повторить"
          onAction={() => void load()}
        />
      ) : null}

      {status ? (
        <div className="mw-referral">
          {banner ? (
            <div className={`mw-referral-banner is-${banner.tone}`} role="status">
              {banner.tone === 'success' ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}
              <span>{banner.text}</span>
            </div>
          ) : null}

          {error ? (
            <button className="mw-referral-refresh" type="button" onClick={() => void load()}>
              Данные могли устареть — обновить
            </button>
          ) : null}

          <section className="mw-referral-hero">
            <div className="mw-referral-hero__glow" aria-hidden="true" />
            <span className="mw-referral-eyebrow"><Gift size={14} />Реферальная программа</span>
            <h3>Бонусы за коллег</h3>
            <p>Пригласите логиста — получите вторую машину бесплатно навсегда. Коллега тоже получит бесплатную машину для старта.</p>
            <div className="mw-referral-hero__metrics">
              <div><Users size={17} /><span><small>Активных коллег</small><strong>{qualifiedCount}</strong></span></div>
              <div><Ticket size={17} /><span><small>Ваш код</small><strong>{status.code || '—'}</strong></span></div>
            </div>
          </section>

          <section className={`mw-referral-main-bonus${mainBonusUnlocked ? ' is-unlocked' : ''}`}>
            <div className="mw-referral-card-head">
              <span>Главный бонус</span>
              <b>{mainBonusUnlocked ? <CheckCircle2 size={14} /> : <Sparkles size={14} />}Навсегда</b>
            </div>
            <div className="mw-referral-main-bonus__title"><Truck size={24} /><h3>2-я машина бесплатно</h3></div>
            <p>{mainBonusUnlocked ? 'Бонус уже активен и добавлен к лимиту машин.' : 'Откроется автоматически после первого активного коллеги.'}</p>
            <div className="mw-referral-progress-head">
              <span>Активных коллег: {Math.min(qualifiedCount, 1)} из 1</span>
              <strong>{mainBonusUnlocked ? 'Получено' : 'Остался 1 коллега'}</strong>
            </div>
            <div className="mw-referral-progress"><i style={{ width: mainBonusUnlocked ? '100%' : '0%' }} /></div>
          </section>

          <section className="mw-referral-card">
            <div className="mw-referral-section-title">
              <span><UserPlus size={19} /></span>
              <div><h3>Отправьте приглашение</h3><p>Ссылка уже содержит ваш персональный код.</p></div>
            </div>
            <div className="mw-referral-link" title={inviteValue}>{inviteValue || 'Ссылка скоро появится'}</div>
            <div className="mw-referral-actions">
              <button type="button" disabled={!inviteValue} onClick={() => void handleCopy()}><Copy size={17} />Скопировать</button>
              <button className="is-primary" type="button" disabled={!inviteValue} onClick={() => void handleShare()}><Share2 size={17} />Поделиться</button>
            </div>
            <div className="mw-referral-colleague-benefit">
              <strong>Коллега получит</strong>
              <span><Check size={13} />1 машину бесплатно навсегда</span>
              <span><Check size={13} />Полный доступ к отслеживанию заявок</span>
            </div>
          </section>

          <section className="mw-referral-card">
            <div className="mw-referral-section-title">
              <span><ArrowRight size={19} /></span>
              <div><h3>Как это работает</h3><p>Три шага до главного бонуса.</p></div>
            </div>
            <ol className="mw-referral-steps">
              <li><b>1</b><span>Скопируйте ссылку и отправьте её коллеге.</span></li>
              <li><b>2</b><span>Коллега зарегистрируется и создаст первое отслеживание.</span></li>
              <li><b>3</b><span>Бонусы начислятся автоматически.</span></li>
            </ol>
          </section>

          <section className="mw-referral-ladder-section">
            <div className="mw-referral-ladder-title">
              <div><h3>Лестница бонусов</h3><p>Чем больше активных коллег, тем больше наград.</p></div>
              {nextTier ? (
                <span>Осталось: {nextTierRemaining} {getColleaguesLabel(nextTierRemaining)}</span>
              ) : (
                <span className="is-complete">Всё открыто</span>
              )}
            </div>
            <div className="mw-referral-ladder">
              {ladder.map((tier) => {
                const current = !tier.reached && tier.threshold === nextTier?.threshold
                const state = tier.reached ? 'completed' : current ? 'current' : 'locked'
                return (
                  <article className={`is-${state}`} key={tier.threshold}>
                    <div><span>{tier.label}</span><b>{tier.reached ? <CheckCircle2 size={15} /> : current ? tier.threshold : <LockKeyhole size={14} />}</b></div>
                    <h4>{tier.title}</h4>
                    <p>{tier.description}</p>
                    <small>{tier.reached ? 'Получено' : current ? 'Следующий бонус' : 'Впереди'}</small>
                  </article>
                )
              })}
            </div>
          </section>

          {status.bonus_slots > 0 || status.tracking_credits > 0 || status.coupons.length > 0 ? (
            <section className="mw-referral-card mw-referral-earned">
              <div className="mw-referral-section-title">
                <span><Gift size={19} /></span>
                <div><h3>Ваши начисления</h3><p>Уже доступны в аккаунте.</p></div>
              </div>
              <div className="mw-referral-earned__grid">
                <div><small>Постоянные машины</small><strong>+{status.bonus_slots}</strong></div>
                <div><small>Отслеживания</small><strong>{status.tracking_credits}</strong></div>
                {status.coupons.map((coupon) => <div key={coupon.code}><small>Купон на тариф</small><strong>{coupon.label}</strong></div>)}
              </div>
            </section>
          ) : null}

          <section className="mw-referral-card mw-referral-rules">
            <h3>Когда приглашение считается активным?</h3>
            <p>Приглашение засчитывается, когда коллега зарегистрируется по вашей ссылке и завершит первую заявку с реальным маршрутом. Бонусы за 2, 3 и 5 приглашённых коллег открываются после оплаты ими тарифа и применяются к следующему месяцу. Для 10 и более коллег действуют индивидуальные условия.</p>
          </section>

          {/* Only a new manager can be invited: the field is offered while the server says so. */}
          {status.can_claim ? (
            <section className="mw-referral-card">
              <div className="mw-referral-section-title">
                <span><Ticket size={19} /></span>
                <div><h3>Вас пригласили?</h3><p>{REFERRAL_CLAIM_HINT}</p></div>
              </div>
              <div className="mw-referral-claim">
                <input
                  value={claimCode}
                  onChange={(event) => setClaimCode(event.target.value)}
                  placeholder="ref_..."
                  aria-label="Код пригласившего"
                  autoCapitalize="none"
                  autoComplete="off"
                />
                <button type="button" disabled={!claimCode.trim() || claiming} onClick={() => void handleClaim()}>
                  {claiming ? 'Применяем…' : 'Применить'}
                </button>
              </div>
            </section>
          ) : status.referred ? (
            <section className="mw-referral-card">
              <div className="mw-referral-section-title">
                <span><Ticket size={19} /></span>
                <div><h3>Приглашение</h3><p>{REFERRAL_LINKED_NOTE}</p></div>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}
    </MobileSheet>
  )
}
